/**
 * Session store backed by sessionStorage (FR-030 — does not survive tab close).
 *
 * Holds the access token, refresh token, and Me. Provides a refresh() that
 * calls /api/v1/auth/refresh and replaces the pair. On any refresh failure
 * the store clears, which makes the layout-level auth gate redirect to /login.
 */

import { writable, get, type Writable } from "svelte/store";
import type { Me, Zone } from "$lib/api/auth";

const KEY = "filar.session.v1";

export type Session = {
  access_token: string;
  refresh_token: string;
  zone: Zone | null;
  user: Me;
};

function load(): Session | null {
  if (typeof window === "undefined") return null;
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Session;
  } catch {
    sessionStorage.removeItem(KEY);
    return null;
  }
}

function persist(s: Session | null) {
  if (typeof window === "undefined") return;
  if (s === null) sessionStorage.removeItem(KEY);
  else sessionStorage.setItem(KEY, JSON.stringify(s));
}

export const session: Writable<Session | null> = writable<Session | null>(load());
session.subscribe(persist);

export function setSession(s: Session) {
  session.set(s);
}

export function clearSession() {
  session.set(null);
}

export function getAccessToken(): string | null {
  const s = get(session);
  return s?.access_token ?? null;
}

export function getRefreshToken(): string | null {
  const s = get(session);
  return s?.refresh_token ?? null;
}

// A27: el backend rota el refresh_token y detecta replay (dos usos del mismo
// token → revoca TODA la cadena). La página de turno tiene varios pollers en
// paralelo; si cada uno dispara su propio refresh al recibir 401, el segundo
// llega con el token ya rotado por el primero → replay → logout forzado en
// pleno turno. Single-flight: todos los llamantes concurrentes comparten la
// MISMA promesa; solo el primero pega al backend, el resto espera su resultado.
let inflightRefresh: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = doRefresh().finally(() => {
    inflightRefresh = null;
  });
  return inflightRefresh;
}

async function doRefresh(): Promise<boolean> {
  const refresh_token = getRefreshToken();
  if (!refresh_token) {
    // UXP-3: sin refresh_token no hay forma de renovar. Si aún hay una sesión
    // colgada (access token vencido, refresh perdido), la limpiamos para que el
    // gate del layout redirija a /login en vez de loopear "Sesión vencida".
    clearSession();
    return false;
  }
  try {
    const resp = await fetch("/api/v1/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "include",
      body: JSON.stringify({ refresh_token }),
    });
    if (!resp.ok) {
      clearSession();
      return false;
    }
    const data = (await resp.json()) as Session;
    setSession(data);
    return true;
  } catch {
    clearSession();
    return false;
  }
}
